import { WppButton, WppTypography } from '@platform-ui-kit/components-library-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCopyFileApi } from 'api/files/mutations/useCopyFileApi'
import { useMoveFileApi } from 'api/files/mutations/useMoveFileApi'
import { useFetchFilesSearchApi } from 'api/files/queryFetchers/useFetchFilesSearchApi'
import { Flex } from 'components/common/flex/Flex'
import { tableActions } from 'components/common/table'
import { SideModal } from 'components/surface/sideModal/SideModal'
import { ApiQueryKeys } from 'constants/apiQueryKeys'
import { TableKey } from 'constants/table'
import { useStableCallback } from 'hooks/useStableCallback'
import { DestinationSelect } from 'pages/files/fileTransferModal/DestinationSelect'
import styles from 'pages/files/fileTransferModal/FileTransferModal.module.scss'
import { showResolveFileConflictModal } from 'pages/files/fileTransferModal/ResolveFileConflictModal'
import { FileTransferType, invalidateDestination, isDestinationNotValid } from 'pages/files/fileTransferModal/utils'
import { useCurrentTenantData } from 'providers/currentTenantData/CurrentTenantDataContext'
import { queryClient } from 'providers/osQueryClient/utils'
import { useToast } from 'providers/toast/ToastProvider'
import { AssignmentType, ConflictResolveEnum, FileItem } from 'types/files/files'
import { Hub } from 'types/hubs/hubs'
import { createNiceModal, NiceModalWrappedProps } from 'utils/createNiceModal'

export interface Props extends NiceModalWrappedProps {
  files: FileItem[]
  transferType?: FileTransferType
}

export const FileTransferModal = ({ id, transferType, onClose, onCloseComplete, isOpen, files }: Props) => {
  const { t } = useTranslation()
  const [selectedHub, setSelectedHub] = useState<Hub | undefined>()
  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>()
  const [isCopying, setIsCopying] = useState(false)
  const { mutateAsync: copyFile } = useCopyFileApi()
  const { mutateAsync: moveFile } = useMoveFileApi()
  const { enqueueToast } = useToast()
  const { currentTenant } = useCurrentTenantData()
  const handleFetchFiles = useFetchFilesSearchApi()
  const isHubSelected = !selectedFolderId && selectedHub?.id
  const isMoving = transferType === FileTransferType.MOVE
  const transferNotAvailable = isCopying || isDestinationNotValid(selectedHub?.id, selectedFolderId, files)

  const refreshFiles = () => {
    if (files.length === 1) {
      queryClient.invalidateQueries({ queryKey: [ApiQueryKeys.FILE, { id: files[0].id }], exact: false })
    }
    tableActions.reload([TableKey.FILES])
  }

  const handleSuccess = (successCount: number, enqueSuccessToast: () => void) => {
    if (successCount > 0) {
      enqueSuccessToast()
    } else {
      enqueueToast({
        type: 'success',
        message: t('os.files.file_transfer_modal.toasts.all_files_skipped'),
      })
    }
  }

  const folderParams = {
    newParentId: selectedFolderId,
  }
  const hubParams = {
    assignmentId: selectedHub?.id,
    assignmentType: AssignmentType.HUB,
  }

  const handleCopy = async (resolveType: ConflictResolveEnum = ConflictResolveEnum.REPLACE) => {
    try {
      const response = await copyFile({
        ids: files.map(({ id }) => id),
        onConflict: resolveType,
        ...(isHubSelected ? hubParams : folderParams),
      })
      handleSuccess(response.data.successIds.length, () =>
        enqueueToast({
          type: 'success',
          message: t('os.files.file_transfer_modal.toasts.file_copied', { count: response.data.successIds.length }),
        }),
      )
      refreshFiles()
    } catch (error) {
      enqueueToast({
        message: t('os.common.errors.error'),
        type: 'error',
      })
    }
    onClose()
  }

  const handleMove = async (resolveType: ConflictResolveEnum = ConflictResolveEnum.REPLACE) => {
    try {
      const response = await moveFile({
        ids: files.map(({ id }) => id),
        onConflict: resolveType,
        ...(isHubSelected ? hubParams : folderParams),
      })
      handleSuccess(response.data.successIds.length, () =>
        enqueueToast({
          type: 'success',
          message: t('os.files.file_transfer_modal.toasts.file_moved', { count: response.data.successIds.length }),
        }),
      )
      refreshFiles()
    } catch (error) {
      enqueueToast({
        message: t('os.common.errors.error'),
        type: 'error',
      })
    }
    onClose()
  }

  const handleFileTransfer = async () => {
    setIsCopying(true)
    try {
      invalidateDestination(selectedFolderId, selectedHub, currentTenant)
      const destinationFiles = await handleFetchFiles({
        parentId: selectedFolderId,
        assignmentId: selectedHub?.id,
        assignmentType: AssignmentType.HUB,
        tenantId: currentTenant?.id,
        recursive: false,
      })
      const conflicts = files.filter(originalFile =>
        destinationFiles.data.some(file => file.name === originalFile.name),
      )
      if (conflicts.length > 0) {
        showResolveFileConflictModal({
          items: files,
          onResolve: async (resolveType: ConflictResolveEnum) => {
            if (transferType === FileTransferType.COPY) {
              await handleCopy(resolveType)
            }
            if (transferType === FileTransferType.MOVE) {
              await handleMove(resolveType)
            }
            setIsCopying(false)
          },
          onCancel: () => {
            setIsCopying(false)
          },
        })
      } else {
        if (transferType === FileTransferType.COPY) {
          await handleCopy()
        }
        if (transferType === FileTransferType.MOVE) {
          await handleMove()
        }
        setIsCopying(false)
      }
      invalidateDestination(selectedFolderId, selectedHub, currentTenant)
    } catch (error) {
      enqueueToast({
        message: t('os.common.errors.error'),
        type: 'error',
      })
      setIsCopying(false)
    }
  }

  const handleCloseComplete = useStableCallback(() => {
    onCloseComplete()
  })

  return (
    <SideModal
      open={isOpen}
      onWppSideModalClose={onClose}
      onWppSideModalCloseComplete={handleCloseComplete}
      disableOutsideClick={isCopying}
      data-testid={id}
      size="m"
    >
      <WppTypography slot="header" type="2xl-heading" data-testid="file-transfer-modal-title">
        {t(`os.files.file_transfer_modal.${isMoving ? 'move_title' : 'copy_title'}`)}
      </WppTypography>
      <Flex slot="body" className={styles.modalBody} direction="column" gap={24}>
        <WppTypography type="l-body" data-testid="file-transfer-modal-file-name">
          {files.length === 1 ? files[0].name : t('os.files.file_transfer_modal.multiple_files')}
        </WppTypography>
        <WppTypography type="l-body" data-testid="file-transfer-modal-destination">
          {t(`os.files.actions.${isMoving ? 'move_to' : 'copy_to'}`)}
        </WppTypography>
        <DestinationSelect
          selectedHub={selectedHub}
          setSelectedHub={setSelectedHub}
          selectedFolderId={selectedFolderId}
          setSelectedFolderId={setSelectedFolderId}
        />
      </Flex>
      <Flex slot="actions" justify="end" gap={12}>
        <WppButton variant="secondary" size="m" onClick={onClose}>
          {t('os.common.cancel')}
        </WppButton>
        <WppButton
          data-testid={`${transferType}-action-button`}
          variant="primary"
          disabled={transferNotAvailable}
          size="m"
          onClick={handleFileTransfer}
          loading={isCopying}
        >
          {t(`os.files.file_transfer_modal.action_button_${isMoving ? 'move' : 'copy'}`)}
        </WppButton>
      </Flex>
    </SideModal>
  )
}

export const { showModal: showFileTransferModal } = createNiceModal(FileTransferModal, 'file-transfer-modal')
